import { memo } from "react";

import { TrendingUp, TrendingDown, Activity } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface MomentumIndicatorProps {
  score: number;
  level: "high" | "medium" | "low";
  size?: "sm" | "md" | "lg";
  showScore?: boolean;
}

const MomentumIndicatorComponent = ({ 
  score, 
  level, 
  size = "md",
  showScore = true 
}: MomentumIndicatorProps) => {
  const getConfig = () => {
    switch (level) {
      case "high":
        return {
          icon: TrendingUp,
          label: "High Momentum",
          variant: "default" as const,
          className: "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20",
        };
      case "medium":
        return {
          icon: Activity,
          label: "Moderate Momentum",
          variant: "secondary" as const,
          className: "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20",
        };
      case "low":
        return {
          icon: TrendingDown,
          label: "Needs Attention",
          variant: "outline" as const,
          className: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20",
        };
    }
  };

  const config = getConfig();
  const Icon = config.icon;
  
  const sizeClasses = {
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5",
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant={config.variant}
            className={`${config.className} flex items-center gap-1.5`}
          >
            <Icon className={sizeClasses[size]} />
            {showScore && (
              <span className="font-semibold tabular-nums">
                {Math.round(score)}
              </span>
            )}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1">
            <p className="font-semibold">{config.label}</p>
            <p className="text-xs text-muted-foreground">
              Score: {score.toFixed(1)} / 100
            </p>
            <p className="text-xs text-muted-foreground">
              {level === "high" && "Project is actively progressing"}
              {level === "medium" && "Some recent activity"}
              {level === "low" && "Consider checking in on this project"}
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const MomentumIndicator = memo(MomentumIndicatorComponent);
